import TextInput from '__components/Inputs/TextInput';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useFlashTransition } from '__hooks/useFlashState';
import { usePreserveCaretPosition } from '__hooks/usePreserveCaretPosition';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_NAME_LENGTH = 100;

export const ExportConfigurationModal: FC<{
  onHide: () => void;
  onConfirm: (prefix: string) => void;
}> = ({ onHide, onConfirm }) => {
  const { t } = useTranslation();

  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const inputRef = usePreserveCaretPosition(value);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const handleChange = (value: string) => {
    cancelFlash();
    const normalizedValue = value.replaceAll(' ', '_');
    setValue(normalizedValue);
    if (error) {
      setError('');
    }
  };

  const handleBlur = () => {
    let nameError = '';
    if (!value) {
      setError('');
      return;
    }
    if (value.length > MAX_NAME_LENGTH) {
      nameError = t('Maximum name size is {{maxLength}} characters.', {
        maxLength: MAX_NAME_LENGTH,
      });
    } else if (!/^[a-zA-Z0-9_.-]*$/.test(value)) {
      nameError = t(
        'Must contain only latin letters, numbers, hyphens, dots and underscores.'
      );
    } else if (!/^[a-zA-Z0-9]/.test(value) || !/[a-zA-Z0-9]$/.test(value)) {
      nameError = t('Must begin and end with a letter or a number.');
    }
    if (nameError) {
      setError(nameError);
      flashErrorMessage(nameError);
    } else {
      setError('');
    }
  };

  const handleConfirm = () => {
    return onConfirm(value);
  };

  return (
    <BasicModal
      heading={t('Export Configuration')}
      buttonText={t('Export')}
      defaultLeftBtnText={t('Cancel')}
      show
      onHide={onHide}
      onConfirm={handleConfirm}
      showLoadingIndicator
      disabled={!!error}
    >
      <TextInput
        inputRef={inputRef}
        label={t('Filename Prefix (Optional)')}
        labelInfo={t(
          'Only latin letters, numbers, hyphens, dots and underscores are allowed.'
        )}
        labelInfoPlacement="top"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={t('Enter a prefix for the export filename')}
        data-qa="export-filename-prefix-input"
        error={!!error}
        validate={{ message, showMessage }}
        inModal
      />
    </BasicModal>
  );
};
